import { useMemo, useState } from 'react';
import { useT } from '../i18n';

interface AttendanceEntry {
  attendance_date: string;
  hours: number | null;
  activity_name: string | null;
}

interface DayData {
  totalHours: number;
  activities: string[];
}

interface AttendanceHeatmapProps {
  data: AttendanceEntry[];
}

// Suffixes for the chart.weekday.* / chart.month.* i18n keys (chart.month.* shared with WeeklyChart).
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri'];
const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
// Full-week suffixes (Sun..Sat) for the compact tooltip date, e.g. "lun 16 jan".
const WEEKDAY_SHORT_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/** High-contrast gradient: --bg-inset (empty) → --accent at increasing intensity, up to 8 h */
function getHeatColor(hours: number | undefined, hasData: boolean): string {
  if (!hasData) return 'var(--bg-inset)';
  if (hours === undefined || hours === 0) return 'color-mix(in srgb, var(--accent) 8%, var(--surface))';
  if (hours <= 1) return 'color-mix(in srgb, var(--accent) 20%, var(--surface))';
  if (hours <= 2) return 'color-mix(in srgb, var(--accent) 35%, var(--surface))';
  if (hours <= 4) return 'color-mix(in srgb, var(--accent) 50%, var(--surface))';
  if (hours <= 6) return 'color-mix(in srgb, var(--accent) 68%, var(--surface))';
  if (hours <= 8) return 'color-mix(in srgb, var(--accent) 82%, var(--surface))';
  return 'var(--accent)'; // 8+ h = full accent
}

function toLocalDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtH(h: number): string {
  return h % 1 === 0 ? `${h} h` : `${h.toFixed(1).replace('.', ',')} h`;
}

export function AttendanceHeatmap({ data }: AttendanceHeatmapProps) {
  const { t } = useT();
  const [tooltip, setTooltip] = useState<{ x: number; y: number; date: string; info: DayData; below: boolean } | null>(null);

  const dateMap = useMemo(() => {
    const map = new Map<string, DayData>();
    for (const d of data) {
      const key = d.attendance_date?.slice(0, 10);
      if (!key) continue;
      const hours = Number(d.hours) || 0;
      const existing = map.get(key);
      if (existing) {
        existing.totalHours += hours;
        if (d.activity_name && !existing.activities.includes(d.activity_name))
          existing.activities.push(d.activity_name);
      } else {
        map.set(key, { totalHours: hours, activities: d.activity_name ? [d.activity_name] : [] });
      }
    }
    return map;
  }, [data]);

  const { weeks, monthMarkers, monthStartCols } = useMemo(() => {
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - 52 * 7);
    while (startDate.getDay() !== 1) startDate.setDate(startDate.getDate() + 1);

    const weeks: string[][] = [];
    const monthMarkers: { col: number; label: string }[] = [];
    let lastMonth = -1;
    const current = new Date(startDate);

    while (current <= today) {
      const week: string[] = [];
      for (let d = 0; d < 7; d++) {
        week.push(toLocalDateStr(current));
        if (d === 0) {
          const m = current.getMonth();
          if (m !== lastMonth) {
            monthMarkers.push({ col: weeks.length, label: t(`chart.month.${MONTH_KEYS[m]}`) });
            lastMonth = m;
          }
        }
        current.setDate(current.getDate() + 1);
      }
      weeks.push(week);
    }
    const monthStartCols = new Set(monthMarkers.map(m => m.col));
    return { weeks, monthMarkers, monthStartCols };
  }, [t]);

  const todayStr = toLocalDateStr(new Date());
  const cs = 12;
  const g = 2;
  const lw = 30;

  const fmtTooltipDate = (s: string) => {
    const [y, m, d] = s.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return `${t(`chart.weekdayShort.${WEEKDAY_SHORT_KEYS[dt.getDay()]}`)} ${d} ${t(`chart.month.${MONTH_KEYS[m - 1]}`)}`;
  };

  return (
    <div className="heatmap-container" style={{ position: 'relative', overflowX: 'auto' }}>
      {/* Month labels */}
      <div style={{ display: 'flex', marginLeft: lw, marginBottom: 2, height: 14, position: 'relative' }}>
        {monthMarkers.map((m, i) => (
          <span key={i} style={{ position: 'absolute', left: m.col * (cs + g), fontSize: 'var(--t-10)', color: 'var(--ink-3)', fontWeight: 500 }}>
            {m.label}
          </span>
        ))}
      </div>

      {/* Grid */}
      <div style={{ display: 'flex' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: g, width: lw, flexShrink: 0 }}>
          {DAY_KEYS.map((key, i) => (
            <div key={i} style={{ height: cs, display: 'flex', alignItems: 'center', fontSize: 'var(--t-10)', color: 'var(--ink-3)', fontWeight: 500 }}>
              {t(`chart.weekday.${key}`)}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: g }}>
          {weeks.map((week, wi) => (
            <div
              key={wi}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: g,
                position: 'relative',
              }}
            >
              {wi > 0 && monthStartCols.has(wi) && (
                <div
                  style={{
                    position: 'absolute',
                    left: -Math.ceil(g / 2) - 0.5,
                    top: -2,
                    bottom: -2,
                    width: 1,
                    background: 'var(--line)',
                    pointerEvents: 'none',
                  }}
                />
              )}
              {week.slice(0, 5).map((dateStr, di) => {
                if (dateStr > todayStr) return <div key={dateStr} style={{ width: cs, height: cs }} />;
                const dd = dateMap.get(dateStr);
                const bg = getHeatColor(dd?.totalHours, !!dd);
                return (
                  <div
                    key={dateStr}
                    style={{ width: cs, height: cs, borderRadius: 'var(--r1)', backgroundColor: bg, cursor: dd ? 'pointer' : 'default' }}
                    onMouseEnter={(e) => {
                      if (!dd) return;
                      const r = (e.target as HTMLElement).getBoundingClientRect();
                      const c = (e.target as HTMLElement).closest('.heatmap-container')!.getBoundingClientRect();
                      // Show tooltip below if cell is in the top 2 rows (Lun/Mar)
                      const showBelow = di <= 1;
                      setTooltip({
                        x: r.left - c.left + cs / 2,
                        y: showBelow ? r.bottom - c.top + 4 : r.top - c.top - 4,
                        date: dateStr,
                        info: dd,
                        below: showBelow,
                      });
                    }}
                    onMouseLeave={() => setTooltip(null)}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 'var(--t-10)', color: 'var(--ink-3)' }}>
        <span>0 h</span>
        <div style={{ display: 'flex', gap: 1, overflow: 'hidden' }}>
          {[
            'color-mix(in srgb, var(--accent) 8%, var(--surface))',
            'color-mix(in srgb, var(--accent) 20%, var(--surface))',
            'color-mix(in srgb, var(--accent) 35%, var(--surface))',
            'color-mix(in srgb, var(--accent) 50%, var(--surface))',
            'color-mix(in srgb, var(--accent) 68%, var(--surface))',
            'color-mix(in srgb, var(--accent) 82%, var(--surface))',
            'var(--accent)',
          ].map((c, i) => (
            <div key={i} style={{ width: 14, height: 9, backgroundColor: c }} />
          ))}
        </div>
        <span>8+ h</span>
        <span style={{ marginLeft: 8 }}>
          <span style={{ display: 'inline-block', width: 9, height: 9, backgroundColor: 'var(--bg-inset)', marginRight: 4, verticalAlign: 'middle' }} />
          {t('chart.legend.noAttendance')}
        </span>
      </div>

      {/* Tooltip — positioned above or below depending on row */}
      {tooltip && (
        <div className="heatmap-tooltip" style={{
          position: 'absolute',
          left: tooltip.x,
          top: tooltip.y,
          transform: tooltip.below ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
          pointerEvents: 'none',
          zIndex: 10,
        }}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>{fmtTooltipDate(tooltip.date)}</div>
          <div>{fmtH(tooltip.info.totalHours)}</div>
          {tooltip.info.activities.length > 0 && (
            <div style={{ marginTop: 2, color: 'var(--ink-3)', fontSize: 'var(--t-10)' }}>
              {tooltip.info.activities.join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
